import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type FC,
  type ReactNode,
} from "react";
import { useSelector } from "@legendapp/state/react";
import { MultiSelect } from "primereact/multiselect";
import { Skeleton } from "primereact/skeleton";
import CampaignDropdownItem, {
  type CampaignOption,
} from "@/layouts/AppLayout/CampaignDropdownItem";
import {
  formatCampaignDateRange,
  getCampaignLifecycleStatus,
  type CampaignLifecycleStatus,
} from "@/layouts/AppLayout/campaignLifecycle";
import EmptyState from "@/layouts/AppLayout/EmptyState";
import useFilterReset, {
  type PanelHeaderTemplateOptions,
} from "@/hooks/useFilterReset";
import {
  AdvertisersStore$,
  clearAdvertisers,
  fetchAdvertisers,
} from "@/stores/advertisersStore";
import { AgenciesStore$ } from "@/stores/agenciesStore";
import {
  CampaignsStore$,
  clearCampaigns,
  fetchCampaigns,
} from "@/stores/campaignsStore";
import { getDefaultOrganizationId } from "@/utils/organization";
import { FilterStore$ } from "@/stores/filterStore";
import { isAgencyUser } from "@/stores/userStore";
import { TEXT } from "@/constants/text";
import {
  campaignPillPt,
  pillPt,
} from "@/layouts/AppLayout/HierarchyBreadcrumb.pt";
import styles from "@/layouts/AppLayout/HierarchyBreadcrumb.module.css";

const VIRTUAL_SCROLLER_OPTIONS = { itemSize: 42 };
const CAMPAIGN_VIRTUAL_SCROLLER_OPTIONS = { itemSize: 56 };
const SCROLL_HEIGHT = "22rem";
const CAMPAIGN_SCROLL_HEIGHT = "26rem";

const getPlaceholder = ({
  hasError,
  isEmpty,
  errorText,
  emptyText,
  defaultText,
}: {
  hasError: boolean;
  isEmpty: boolean;
  errorText: string;
  emptyText: string;
  defaultText: string;
}) => {
  if (hasError) return errorText;
  if (isEmpty) return emptyText;
  return defaultText;
};

const renderCampaignItem = (option: CampaignOption) => (
  <CampaignDropdownItem option={option} />
);

type CampaignStatusFilter = "all" | CampaignLifecycleStatus;

const CAMPAIGN_STATUS_FILTERS: {
  key: CampaignStatusFilter;
  label: string;
}[] = [
  { key: "all", label: TEXT.hierarchyBreadcrumb.campaignStatusAll },
  { key: "active", label: TEXT.hierarchyBreadcrumb.badgeActive },
  { key: "upcoming", label: TEXT.hierarchyBreadcrumb.badgeUpcoming },
  { key: "ended", label: TEXT.hierarchyBreadcrumb.badgeEnded },
];

// MultiSelect's `emptyMessage` prop is typed as `string`, but at runtime it's
// passed through ObjectUtils.getJSXElement, which renders any ReactNode just
// like `emptyFilterMessage` (typed correctly as ReactNode) does.
const asEmptyMessage = (node: ReactNode) => node as unknown as string;

const Separator: FC = () => (
  <svg
    className={styles.separator}
    fill="none"
    height="14"
    stroke="currentColor"
    strokeLinecap="round"
    strokeWidth="2"
    viewBox="0 0 24 24"
    width="14"
  >
    <polyline points="9 18 15 12 9 6" />
  </svg>
);

const HierarchyBreadcrumb: FC = () => {
  const agency = useSelector(() => FilterStore$.agency.get());
  const advertiser = useSelector(() => FilterStore$.advertiser.get());
  const campaign = useSelector(() => FilterStore$.campaign.get());

  const agencyUser = useSelector(() => isAgencyUser());

  const agencies = useSelector(() => AgenciesStore$.agencies.get());
  const agenciesLoading = useSelector(() => AgenciesStore$.isLoading.get());
  const agenciesError = useSelector(() => AgenciesStore$.error.get());
  const agenciesLoadedForOrganizationId = useSelector(() =>
    AgenciesStore$.loadedForOrganizationId.get()
  );

  const advertisers = useSelector(() => AdvertisersStore$.advertisers.get());
  const advertisersLoading = useSelector(() =>
    AdvertisersStore$.isLoading.get()
  );
  const advertisersError = useSelector(() => AdvertisersStore$.error.get());
  const advertisersLoadedKey = useSelector(() =>
    AdvertisersStore$.loadedForScopeKey.get()
  );

  const campaigns = useSelector(() => CampaignsStore$.campaigns.get());
  const campaignsLoading = useSelector(() => CampaignsStore$.isLoading.get());
  const campaignsError = useSelector(() => CampaignsStore$.error.get());

  const [campaignStatusFilter, setCampaignStatusFilter] =
    useState<CampaignStatusFilter>("all");

  const agencyFilterReset = useFilterReset();
  const advertiserFilterReset = useFilterReset();
  const campaignFilterReset = useFilterReset(styles.campaignHeaderNoDivider);
  const hasDefaultedAgencies = useRef(false);
  const hasDefaultedAdvertisers = useRef(false);
  const hasDefaultedCampaigns = useRef(false);
  const previousCampaignIds = useRef<Set<number>>(new Set());

  const agencyOptions = useMemo(
    () => agencies.map((option) => ({ label: option.name, value: option.id })),
    [agencies]
  );

  const advertiserOptions = useMemo(
    () =>
      advertisers.map((option) => ({ label: option.name, value: option.id })),
    [advertisers]
  );

  // Formatting a date range per campaign is the expensive part, so it's the
  // only part memoized on the campaign data itself — not on every render,
  // e.g. the one triggered by clicking an option in the Agency list.
  // lifecycleStatus is deliberately computed outside the memo, against a
  // fresh `now` every render: it's a cheap comparison, and memoizing it
  // alongside the date range would freeze a campaign's active/ended/upcoming
  // status at whatever "now" was on the last campaigns-array change, going
  // stale if the app stays open across the moment a campaign's status
  // actually flips.
  const campaignDateRanges = useMemo(
    () =>
      campaigns.map((option) => ({
        label: option.name,
        value: option.id,
        duration: option.duration,
        dateRangeLabel: formatCampaignDateRange(
          option.duration,
          option.zone_name
        ),
      })),
    [campaigns]
  );

  const campaignOptions: CampaignOption[] = campaignDateRanges.map(
    ({ duration, ...option }) => ({
      ...option,
      lifecycleStatus: getCampaignLifecycleStatus(duration, new Date()),
    })
  );

  const visibleCampaignOptions =
    campaignStatusFilter === "all"
      ? campaignOptions
      : campaignOptions.filter(
          (option) => option.lifecycleStatus === campaignStatusFilter
        );

  const renderCampaignPanelHeader = (options: PanelHeaderTemplateOptions) => (
    <>
      {campaignFilterReset.buildHeader(options)}
      <div className={styles.statusFilters}>
        {CAMPAIGN_STATUS_FILTERS.map(({ key, label }) => (
          <button
            key={key}
            className={
              key === campaignStatusFilter
                ? `${styles.statusFilterChip} ${styles.statusFilterChipActive}`
                : styles.statusFilterChip
            }
            type="button"
            onClick={() => setCampaignStatusFilter(key)}
          >
            {label}
          </button>
        ))}
      </div>
    </>
  );

  const agencyPlaceholder = agenciesError
    ? TEXT.hierarchyBreadcrumb.agencyError
    : TEXT.hierarchyBreadcrumb.agencyPlaceholder;

  const advertiserPlaceholder = getPlaceholder({
    hasError: Boolean(advertisersError),
    isEmpty:
      agency.length > 0 &&
      !advertisersLoading &&
      advertiserOptions.length === 0,
    errorText: TEXT.hierarchyBreadcrumb.advertiserError,
    emptyText: TEXT.hierarchyBreadcrumb.advertiserEmpty,
    defaultText: TEXT.hierarchyBreadcrumb.advertiserPlaceholder,
  });

  const campaignPlaceholder = getPlaceholder({
    hasError: Boolean(campaignsError),
    isEmpty:
      (agency.length > 0 || advertiser.length > 0) &&
      !campaignsLoading &&
      campaignOptions.length === 0,
    errorText: TEXT.hierarchyBreadcrumb.campaignError,
    emptyText: TEXT.hierarchyBreadcrumb.campaignEmpty,
    defaultText: TEXT.hierarchyBreadcrumb.campaignPlaceholder,
  });

  const handleAgencyChange = (value: number[]) => {
    hasDefaultedCampaigns.current = false;
    hasDefaultedAdvertisers.current = false;
    // Clear synchronously so advertiserOptions drops to [] before the
    // hasDefaultedAdvertisers effect can fire with the previous agency's rows.
    // Also cancel any in-flight campaign fetch so a stale result from the
    // previous agency cannot commit and auto-select the wrong campaigns.
    clearAdvertisers();
    clearCampaigns();
    FilterStore$.assign({ agency: value, advertiser: [], campaign: [] });
  };

  const handleAdvertiserChange = (value: number[]) => {
    FilterStore$.assign({ advertiser: value });
  };

  useEffect(() => {
    if (hasDefaultedAgencies.current) return;
    if (agencies.length > 0) {
      hasDefaultedAgencies.current = true;
      FilterStore$.agency.set(
        agencyUser ? [agencies[0].id] : agencies.map((item) => item.id)
      );
      // Set alongside the default so the page never sees "agencies loaded but
      // nothing selected yet" and flashes its empty state.
      AgenciesStore$.agencyInitialized.set(true);
    } else if (
      !agenciesLoading &&
      (agenciesLoadedForOrganizationId !== null || Boolean(agenciesError)) &&
      !agencyUser
    ) {
      // Settled with no agencies (or a failed fetch) for an Infillion user —
      // nothing will ever be defaulted, so the empty state is now the correct
      // view rather than an endless skeleton.
      AgenciesStore$.agencyInitialized.set(true);
    } else if (
      !agenciesLoading &&
      (agenciesLoadedForOrganizationId !== null || Boolean(agenciesError)) &&
      agencyUser
    ) {
      // Agency fetch settled with no results, or failed — the advertiser
      // cascade will never start, so advance advertiserInitialized now so the
      // page can show its empty state rather than spinning indefinitely.
      //
      // loadedForOrganizationId is what distinguishes "settled with none" from
      // "not asked yet": an empty list and !isLoading is also the initial
      // state. This effect runs before AppLayout's fetch even starts, because
      // React flushes a child's effects before its parent's — so without this
      // check it latched hasDefaultedAgencies on the first render and the real
      // agencies, arriving later, were ignored. Every agency user then got a
      // page that loaded nothing at all.
      //
      // A failure doesn't latch: a later successful fetch must still be
      // defaulted.
      hasDefaultedAgencies.current = !agenciesError;
      AgenciesStore$.agencyInitialized.set(true);
      AdvertisersStore$.advertiserInitialized.set(true);
    }
  }, [
    agencies,
    agenciesError,
    agenciesLoading,
    agenciesLoadedForOrganizationId,
    agencyUser,
  ]);

  useEffect(() => {
    if (hasDefaultedAdvertisers.current) return;
    if (advertiserOptions.length > 0) {
      hasDefaultedAdvertisers.current = true;
      FilterStore$.advertiser.set(
        advertiserOptions.map((option) => option.value)
      );
      AdvertisersStore$.advertiserInitialized.set(true);
    } else if (!advertisersLoading && advertisersLoadedKey !== null) {
      // Fetch completed but returned no advertisers — still advance the
      // cascade so downstream effects and empty-state checks can proceed.
      hasDefaultedAdvertisers.current = true;
      AdvertisersStore$.advertiserInitialized.set(true);
    }
  }, [advertiserOptions, advertisersLoading, advertisersLoadedKey]);

  useEffect(() => {
    if (agency.length === 0) {
      clearAdvertisers();
      return;
    }
    fetchAdvertisers({
      organizationId: getDefaultOrganizationId(),
      agencyIds: agency,
    });
  }, [agency]);

  useEffect(() => {
    if (agency.length === 0) {
      clearCampaigns();
      return;
    }
    // Defer until the advertiser default has run: once it fires it sets
    // `advertiser`, which re-triggers this effect with the correct scope.
    // Without this guard the effect would launch an agency-scoped fetch
    // immediately, then a second fetch seconds later when advertiser IDs land.
    if (!hasDefaultedAdvertisers.current) return;
    // When options have loaded but nothing is selected, treat as empty state.
    if (advertiserOptions.length > 0 && advertiser.length === 0) {
      clearCampaigns();
      return;
    }
    // When all advertisers are selected, use the cheaper agency-scoped query.
    const allAdvertisersSelected =
      advertiserOptions.length > 0 &&
      advertiser.length === advertiserOptions.length;
    // Every agency selected: query by the organization rather than listing
    // each agency id. Agency users never do — the organization would include
    // agencies they don't own.
    const allAgenciesSelected =
      !agencyUser && agencies.length > 0 && agency.length === agencies.length;
    fetchCampaigns({
      organizationId: getDefaultOrganizationId(),
      agencyIds: allAgenciesSelected ? [] : agency,
      advertiserIds: allAdvertisersSelected ? [] : advertiser,
    });
  }, [agency, advertiser, advertiserOptions, agencies, agencyUser]);

  useEffect(() => {
    if (campaigns.length === 0) {
      // If we've already defaulted once, a settled-empty result (no campaigns
      // for the new scope, or an error clear) means the stale selection should
      // be cleared — otherwise CampaignOverviewPage stays in isTransitioning
      // indefinitely. Skip this during the agency-change transition: at that
      // point hasDefaultedCampaigns is already false, so the guard prevents the
      // brief empty-state flash that Copilot's unconditional set([]) would cause.
      if (hasDefaultedCampaigns.current) {
        FilterStore$.campaign.set([]);
        previousCampaignIds.current = new Set();
        hasDefaultedCampaigns.current = false;
      }
      return;
    }

    const newIds = new Set(campaigns.map((item) => item.id));

    if (!hasDefaultedCampaigns.current) {
      hasDefaultedCampaigns.current = true;
      previousCampaignIds.current = newIds;
      FilterStore$.campaign.set(campaigns.map((item) => item.id));
      return;
    }

    // Campaign list changed (advertiser filter changed).
    // Auto-select campaigns that are new to this list; drop campaigns that are
    // no longer available; keep the user's explicit selection for the rest.
    const currentSelection = new Set(FilterStore$.campaign.get());
    const newSelection = campaigns
      .map((item) => item.id)
      .filter(
        (id) => !previousCampaignIds.current.has(id) || currentSelection.has(id)
      );
    previousCampaignIds.current = newIds;
    // Fallback to all-selected only when the user had campaigns selected but
    // all of them disappeared from the new scope (scope narrowed fully). When
    // currentSelection is already empty the user explicitly cleared it — keep [].
    let nextSelection: number[];
    if (newSelection.length > 0) {
      nextSelection = newSelection;
    } else if (currentSelection.size > 0) {
      nextSelection = campaigns.map((item) => item.id);
    } else {
      nextSelection = [];
    }
    FilterStore$.campaign.set(nextSelection);
  }, [campaigns]);

  return (
    <div className={styles.breadcrumb}>
      {!agencyUser && (
        <>
          {agenciesLoading ? (
            <Skeleton
              height="2.188rem"
              pt={{ root: { className: styles.pillSkeleton } }}
            />
          ) : (
            <MultiSelect
              disabled={Boolean(agenciesError)}
              emptyFilterMessage={
                <EmptyState title={TEXT.hierarchyBreadcrumb.noMatchesFound} />
              }
              emptyMessage={asEmptyMessage(
                <EmptyState title={TEXT.hierarchyBreadcrumb.agencyEmpty} />
              )}
              filter
              filterPlaceholder={
                TEXT.hierarchyBreadcrumb.searchAgenciesPlaceholder
              }
              filterTemplate={agencyFilterReset.filterTemplate}
              maxSelectedLabels={1}
              options={agencyOptions}
              panelHeaderTemplate={agencyFilterReset.buildHeader}
              placeholder={agencyPlaceholder}
              pt={pillPt}
              scrollHeight={SCROLL_HEIGHT}
              selectedItemsLabel={
                TEXT.hierarchyBreadcrumb.agencySelectedItemsLabel
              }
              showClear
              value={agency}
              virtualScrollerOptions={VIRTUAL_SCROLLER_OPTIONS}
              onChange={(e) => handleAgencyChange(e.value)}
              onFilter={agencyFilterReset.onFilter}
            />
          )}
          <Separator />
        </>
      )}
      {advertisersLoading && advertiserOptions.length === 0 ? (
        <Skeleton
          height="2.188rem"
          pt={{ root: { className: styles.pillSkeleton } }}
        />
      ) : (
        <MultiSelect
          disabled={
            agency.length === 0 ||
            Boolean(advertisersError) ||
            advertiserOptions.length === 0
          }
          emptyFilterMessage={
            <EmptyState title={TEXT.hierarchyBreadcrumb.noMatchesFound} />
          }
          emptyMessage={asEmptyMessage(
            <EmptyState
              hint={
                agency.length > 0
                  ? TEXT.hierarchyBreadcrumb.advertiserEmptyHint
                  : undefined
              }
              title={TEXT.hierarchyBreadcrumb.advertiserEmpty}
            />
          )}
          filter
          filterPlaceholder={
            TEXT.hierarchyBreadcrumb.searchAdvertisersPlaceholder
          }
          filterTemplate={advertiserFilterReset.filterTemplate}
          maxSelectedLabels={1}
          options={advertiserOptions}
          panelHeaderTemplate={advertiserFilterReset.buildHeader}
          placeholder={advertiserPlaceholder}
          pt={pillPt}
          scrollHeight={SCROLL_HEIGHT}
          selectedItemsLabel={
            TEXT.hierarchyBreadcrumb.advertiserSelectedItemsLabel
          }
          showClear
          value={advertiser}
          virtualScrollerOptions={VIRTUAL_SCROLLER_OPTIONS}
          onChange={(e) => handleAdvertiserChange(e.value)}
          onFilter={advertiserFilterReset.onFilter}
        />
      )}
      <Separator />
      {campaignsLoading && campaignOptions.length === 0 ? (
        <Skeleton
          height="2.188rem"
          pt={{
            root: {
              className: `${styles.pillSkeleton} ${styles.campaignPillSkeleton}`,
            },
          }}
        />
      ) : (
        <MultiSelect
          disabled={
            (agency.length === 0 && advertiser.length === 0) ||
            Boolean(campaignsError) ||
            campaignOptions.length === 0
          }
          emptyFilterMessage={
            <EmptyState title={TEXT.hierarchyBreadcrumb.noMatchesFound} />
          }
          emptyMessage={asEmptyMessage(
            <EmptyState
              hint={
                agency.length > 0 || advertiser.length > 0
                  ? TEXT.hierarchyBreadcrumb.campaignEmptyHint
                  : undefined
              }
              title={TEXT.hierarchyBreadcrumb.campaignEmpty}
            />
          )}
          filter
          filterPlaceholder={
            TEXT.hierarchyBreadcrumb.searchCampaignsPlaceholder
          }
          filterTemplate={campaignFilterReset.filterTemplate}
          itemTemplate={renderCampaignItem}
          maxSelectedLabels={1}
          options={visibleCampaignOptions}
          panelHeaderTemplate={renderCampaignPanelHeader}
          placeholder={campaignPlaceholder}
          pt={campaignPillPt}
          scrollHeight={CAMPAIGN_SCROLL_HEIGHT}
          selectedItemsLabel={
            TEXT.hierarchyBreadcrumb.campaignSelectedItemsLabel
          }
          showClear
          value={campaign}
          virtualScrollerOptions={CAMPAIGN_VIRTUAL_SCROLLER_OPTIONS}
          onChange={(e) => FilterStore$.campaign.set(e.value)}
          onFilter={campaignFilterReset.onFilter}
        />
      )}
    </div>
  );
};

export default HierarchyBreadcrumb;
